import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
    Builder_,
    Dropdown_,
    Filled_Edit,
    FormBox,
    FormBoxSelection,
    FormBoxTitle,
    Input_,
    Main_,
    Main_Plain
} from "monica-alexandria";
import { Sales_Lead_Avatar } from "./Sales_Lead_Avatar";
import { Sales_Lead_Comments } from "./Sales_Lead_Comments";
import { Sales_Lead_Progress_Action } from "./Sales_Lead_Progress_Action";
import { Location_Input } from "../../../../components/location/Location_Input";
import { get_business_categories } from "../../newLead/utils/business_categories";
import {
    read_lead_assignee_member,
    read_member_name,
    read_prospect_status,
    prospectLeadStatuses
} from "../utils/sales_helpers";
import { read_brand_input_value } from "../../../../utils/input/brand_input";
import { read_timeline_date_label } from "../../../../utils/timeline/timeline_formatting";

const ProspectLeadFormBody = styled.div`
    display: grid;
    gap: 1.2rem;
`;

const ProspectLeadFormSelection = styled(FormBoxSelection)`
    display: grid;
    gap: 0.4rem;
`;

const ProspectLeadFormTitle = styled(FormBoxTitle)`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ProspectLeadFormActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
`;

const ProspectLeadFormPerson = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
`;

const ProspectLeadSession = styled(FormBox)`
    display: grid;
    gap: 1rem;
`;

const ProspectLeadSessionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;

    h4,
    h6 {
        color: ${p => p.theme.color};
        overflow-wrap: anywhere;
    }
`;

const ProspectLeadSessionStats = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    gap: 0.8rem;
`;

const ProspectLeadSessionStat = styled.div`
    display: grid;
    gap: 0.2rem;

    h6,
    p {
        color: ${p => p.theme.color};
        overflow-wrap: anywhere;
    }
`;

const ProspectLeadSessionEvents = styled.div`
    display: grid;
    gap: 0.6rem;
`;

const ProspectLeadSessionEvent = styled.div`
    display: grid;
    gap: 0.2rem;
    padding: 0.8rem;
    border-radius: var(--smallRadius);
    background: ${p => p.theme.low};

    h6,
    p {
        color: ${p => p.theme.color};
        overflow-wrap: anywhere;
        white-space: pre-wrap;
    }
`;

/**
 * Builds the edited lead payload for the field saved from the slider.
 */
const read_edited_lead = (lead, field, value) => {
    const updatedLead = { ...lead };
    const cleanValue = value.trim();

    if (field === "firstname") {
        updatedLead.firstname = cleanValue;
    }

    if (field === "lastname") {
        updatedLead.lastname = cleanValue;
    }

    if (field === "brand") {
        updatedLead.brand = read_brand_input_value(cleanValue);
    }

    if (field === "category") {
        updatedLead.category = cleanValue;
        updatedLead.subcategory = "";
    }

    if (field === "subcategory") {
        updatedLead.subcategory = cleanValue;
    }

    if (field === "location") {
        updatedLead.location = cleanValue;
    }

    return updatedLead;
};

/**
 * Reads the visible edit value while matching Brand save normalization.
 */
const read_field_edit_value = (field, value) => {
    if (field === "brand") {
        return read_brand_input_value(value);
    }

    return value;
};

/**
 * Reads only canonical preset options for category dropdowns.
 */
const read_select_options = (options) => {
    return (options || []).filter(Boolean);
};

/**
 * Reads unique list values from legacy and current lead contact fields.
 */
const read_contact_list = (...fields) => {
    const values = fields.flatMap((field) => Array.isArray(field) ? field : [field]).filter(Boolean);

    return values.filter((value, index, list) => list.indexOf(value) === index);
};

/**
 * Checks whether a lead has the minimum fields needed for an Agency handoff.
 */
const read_contract_sent_ready = (lead) => {
    const emails = read_contact_list(lead?.email, lead?.emailList, lead?.emails);
    const hasBrand = Boolean(`${lead?.brand || ""}`.trim());
    const hasCategory = Boolean(`${lead?.category || ""}`.trim());

    return hasBrand && emails.length > 0 && hasCategory;
};

const salesSessionEventLabelLookup = {
    to_call: "To Call",
    in_progress: "In Progress",
    stalling: "Stalling",
    lost: "Lost",
    backlog: "Backlog",
    seller: "Seller",
    brand_update: "Brand update",
    comment: "Comment",
    calendar: "Calendar",
    client_link: "Brand identity",
    agency_handoff: "Agency",
    sass_handoff: "SASS",
    sass: "SASS",
    sass_account_preselected: "Account",
    sales_exit: "Closed"
};

/**
 * Reads the active Sales-session draft from the current row or nested Brand record.
 */
const read_active_sales_session = (lead) => {
    return lead?.activeSalesSession || lead?.brandRecord?.activeSalesSession || null;
};

/**
 * Reads the visible label for one live Sales-session nested event.
 */
const read_sales_session_event_label = (event, t) => {
    return t(salesSessionEventLabelLookup[event?.type] || event?.type || "Event");
};

/**
 * Reads useful live Sales-session event text without generic placeholders.
 */
const read_sales_session_event_note = (event) => {
    if (event?.type === "comment") {
        return event?.data?.comment || event?.comment || event?.note || "";
    }

    return event?.note || event?.to?.status || event?.to?.outcome || "";
};

/**
 * Renders the open Sales-session memory before it becomes a shared timeline block.
 */
const Sales_Lead_Form_Session = (props) => {
    const events = [...(props?.session?.events || [])].reverse();

    if (!props?.session?.sessionId) {
        return null;
    }

    return (
        <ProspectLeadSession>
            <ProspectLeadSessionHeader>
                <h4>{props.t("Sales session")}</h4>
                <h6>{props.t("Live")}</h6>
            </ProspectLeadSessionHeader>
            <ProspectLeadSessionStats>
                <ProspectLeadSessionStat>
                    <h6>{props.t("Opened")}</h6>
                    <p>{read_timeline_date_label(props.session?.openedAt)}</p>
                </ProspectLeadSessionStat>
                <ProspectLeadSessionStat>
                    <h6>{props.t("Opened status")}</h6>
                    <p>{props.session?.openedStatus || "-"}</p>
                </ProspectLeadSessionStat>
                <ProspectLeadSessionStat>
                    <h6>{props.t("Last status")}</h6>
                    <p>{props.session?.lastStatus || "-"}</p>
                </ProspectLeadSessionStat>
            </ProspectLeadSessionStats>
            <ProspectLeadSessionEvents>
                {events.map((event, index) => (
                    <ProspectLeadSessionEvent key={event?.sourceKey || `${props.session?.sessionId}-${index}`}>
                        <h6>{[read_timeline_date_label(event?.changedAt), read_sales_session_event_label(event, props.t)].filter(Boolean).join(" - ")}</h6>
                        <p>{read_sales_session_event_note(event) || "-"}</p>
                    </ProspectLeadSessionEvent>
                ))}
            </ProspectLeadSessionEvents>
        </ProspectLeadSession>
    );
};

/**
 * Builds the edited lead payload when adding or removing a list contact.
 */
const read_edited_list_lead = (lead, field, values) => {
    const updatedLead = { ...lead };

    if (field === "email") {
        const emails = values.map((value) => value.toLowerCase());

        updatedLead.email = emails[0] || "";
        updatedLead.emails = emails;
        updatedLead.emailList = [];
    }

    if (field === "phone") {
        const phones = values;

        updatedLead.phone = phones[0] || "";
        updatedLead.phones = phones;
        updatedLead.phoneList = [];
    }

    return updatedLead;
};

/**
 * Renders one editable lead field as its own FormBox inside the slider.
 */
const Sales_Lead_Form_Field = (props) => {
    const editing = props?.editingField === props?.field;

    if (props?.locked) {
        return (
            <FormBox>
                <FormBoxTitle>
                    <h4>{props?.label}</h4>
                </FormBoxTitle>
                <ProspectLeadFormSelection>
                    <p>{props?.value || props?.emptyLabel}</p>
                </ProspectLeadFormSelection>
            </FormBox>
        );
    }

    if (editing) {
        if (props?.field === "location") {
            return (
                <FormBox>
                    <ProspectLeadFormTitle>
                        <h4>{props?.label}</h4>
                        <ProspectLeadFormActions>
                            <Main_ iconLeft="Check" onClick={props?.onDone} />
                        </ProspectLeadFormActions>
                    </ProspectLeadFormTitle>
                    <Builder_>
                        <Location_Input
                            placeholder={props?.label}
                            value={props?.editingValue}
                            onChange={(event) => props?.onDraft(props?.field, event.target.value)}
                        />
                    </Builder_>
                </FormBox>
            );
        }

        return (
            <FormBox>
                <ProspectLeadFormTitle>
                    <h4>{props?.label}</h4>
                    <ProspectLeadFormActions>
                        <Main_ iconLeft="Check" onClick={props?.onDone} />
                    </ProspectLeadFormActions>
                </ProspectLeadFormTitle>
                <Builder_>
                    <Input_
                        placeholder={props?.label}
                        value={props?.editingValue}
                        onChange={(event) => props?.onDraft(props?.field, event.target.value)}
                    />
                </Builder_>
            </FormBox>
        );
    }

    return (
        <FormBox filled onClick={() => props?.onStart(props?.field, props?.value)}>
            <FormBoxTitle>
                <Filled_Edit />
                <h4>{props?.label}</h4>
            </FormBoxTitle>
            <ProspectLeadFormSelection>
                <p>{props?.value || props?.emptyLabel}</p>
            </ProspectLeadFormSelection>
        </FormBox>
    );
};

/**
 * Renders an editable array field with Alexandria's list input behavior.
 */
const Sales_Lead_Form_List_Field = (props) => {
    const editing = props?.editingField === props?.field;
    const value = props?.values?.join(", ");
    const lockedReadonly = props?.locked;

    if (lockedReadonly) {
        return (
            <FormBox>
                <FormBoxTitle>
                    <h4>{props?.label}</h4>
                </FormBoxTitle>
                <ProspectLeadFormSelection>
                    <p>{value || props?.emptyLabel}</p>
                </ProspectLeadFormSelection>
            </FormBox>
        );
    }

    if (editing) {
        return (
            <FormBox>
                <ProspectLeadFormTitle>
                    <h4>{props?.label}</h4>
                    <ProspectLeadFormActions>
                        <Main_ iconLeft="Check" onClick={props?.onDone} />
                    </ProspectLeadFormActions>
                </ProspectLeadFormTitle>
                <Builder_>
                    <Input_
                        placeholder={props?.label}
                        value={props?.editingValue}
                        list={props?.values}
                        icon_ListItem={props?.icon}
                        add={props?.add}
                        onChange={(event) => props?.onDraft(event.target.value)}
                        onAdd={() => props?.onAdd(props?.field, props?.editingValue)}
                        onDelete={(index) => props?.onDelete(props?.field, index)}
                    />
                </Builder_>
            </FormBox>
        );
    }

    return (
        <FormBox filled onClick={() => props?.onStart(props?.field)}>
            <FormBoxTitle>
                <Filled_Edit />
                <h4>{props?.label}</h4>
            </FormBoxTitle>
            <ProspectLeadFormSelection>
                <p>{value || props?.emptyLabel}</p>
            </ProspectLeadFormSelection>
        </FormBox>
    );
};

/**
 * Renders lead status as a standalone dropdown, not a FormBox.
 */
const Sales_Lead_Form_Status = (props) => {
    return (
        <Builder_>
            <Dropdown_ label={props?.label}>
                <select
                    value={props?.value}
                    disabled={props?.locked}
                    onChange={(event) => props?.onStatusChange(event.target.value)}
                >
                    {prospectLeadStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                            {status.label}
                        </option>
                    ))}
                </select>
            </Dropdown_>
        </Builder_>
    );
};

/**
 * Renders a dropdown FormBox for category fields backed by the business prefix tree.
 */
const Sales_Lead_Form_Select_Field = (props) => {
    if (props?.locked) {
        return (
            <FormBox>
                <FormBoxTitle>
                    <h4>{props?.label}</h4>
                </FormBoxTitle>
                <FormBoxSelection>
                    <p>{props?.value || props?.emptyLabel}</p>
                </FormBoxSelection>
            </FormBox>
        );
    }

    return (
        <Builder_>
            <Dropdown_ label={props?.label}>
                <select
                    value={props?.value || ""}
                    onChange={(event) => props?.onChange(props?.field, event.target.value)}
                >
                    <option value="">{props?.emptyLabel}</option>
                    {(props?.options || []).map((option) => (
                        <option key={option} value={option}>
                            {props?.optionLabel ? props.optionLabel(option) : option}
                        </option>
                    ))}
                </select>
            </Dropdown_>
        </Builder_>
    );
};

/**
 * Renders the assigned Growth member as a read-only FormBox field.
 */
const Sales_Lead_Form_Assignee = (props) => {
    return (
        <FormBox>
            <FormBoxTitle>
                <h4>{props?.label}</h4>
            </FormBoxTitle>
            <ProspectLeadFormSelection>
                <ProspectLeadFormPerson>
                    <Sales_Lead_Avatar
                        member={props?.member}
                        label={props?.name}
                        name={props?.name}
                        size="3.2rem"
                    />
                    <p>{props?.name}</p>
                </ProspectLeadFormPerson>
            </ProspectLeadFormSelection>
        </FormBox>
    );
};

/**
 * Renders the clicked Prospect Lead details as form content.
 */
export const Sales_Lead_Form_Edit = (props) => {
    const { t } = useTranslation();
    const [editingField, setEditingField] = useState("");
    const [editingValue, setEditingValue] = useState("");
    const emails = read_contact_list(props?.lead?.email, props?.lead?.emailList, props?.lead?.emails);
    const phones = read_contact_list(props?.lead?.phone, props?.lead?.phoneList, props?.lead?.phones);
    const assigneeMember = read_lead_assignee_member(props?.lead, props?.teamMembers);
    const assigneeName = read_member_name(assigneeMember) || t("Unassigned");
    const status = read_prospect_status(props?.lead);
    const formLocked = !props?.inProgress;
    const businessCategories = get_business_categories((value) => value);
    const categoryOptions = read_select_options(Object.keys(businessCategories), props?.lead?.category);
    const subcategoryOptions = read_select_options(businessCategories[props?.lead?.category] || [], props?.lead?.subcategory);
    const activeSalesSession = read_active_sales_session(props?.lead);

    /**
     * Opens one FormBox in edit mode with the currently visible value.
     */
    const start_field_edit = (field, value) => {
        setEditingField(field);
        setEditingValue(read_field_edit_value(field, value || ""));
    };

    /**
     * Closes edit mode without saving.
     */
    const finish_field_edit = () => {
        setEditingField("");
        setEditingValue("");
    };

    /**
     * Updates the slider draft while the field input is edited.
     */
    const draft_field_edit = (field, value) => {
        const editValue = read_field_edit_value(field, value);

        setEditingValue(editValue);
        props?.onDraftLead(read_edited_lead(props?.lead, field, editValue));
    };

    /**
     * Saves a dropdown selection into the current draft lead.
     */
    const select_field_value = (field, value) => {
        props?.onDraftLead(read_edited_lead(props?.lead, field, value));
    };

    /**
     * Tracks the currently typed list value while preserving local edit state.
     */
    const draft_list_value = (field, value) => {
        setEditingValue(value);

        if (props?.phoneOnly && field === "phone") {
            props?.onPendingPhoneValue(value);
        }
    };

    /**
     * Adds one email or phone value to the slider draft array.
     */
    const add_list_value = (field, value) => {
        const cleanValue = value.trim();
        const currentValues = {
            email: emails,
            phone: phones
        }[field];
        const nextValues = cleanValue ? [...currentValues, cleanValue] : currentValues;
        const updatedLead = read_edited_list_lead(props?.lead, field, nextValues);

        props?.onDraftLead(updatedLead);
        setEditingValue("");

        if (props?.phoneOnly && field === "phone") {
            props?.onPendingPhoneValue("");
            props?.onSaveLead(updatedLead);
        }
    };

    /**
     * Removes one email or phone value from the slider draft array.
     */
    const delete_list_value = (field, index) => {
        const currentValues = {
            email: emails,
            phone: phones
        }[field];
        const nextValues = currentValues.filter((value, valueIndex) => valueIndex !== index);
        const updatedLead = read_edited_list_lead(props?.lead, field, nextValues);

        props?.onDraftLead(updatedLead);

        if (props?.phoneOnly && field === "phone") {
            props?.onPendingPhoneValue("");
            props?.onSaveLead(updatedLead);
        }
    };

    return (
        <ProspectLeadFormBody>
            {props?.inProgress ? 
                <Sales_Lead_Progress_Action
                    action={props?.progressAction}
                    date={props?.progressActionDate}
                    comment={props?.progressActionComment}
                    error={props?.progressActionError}
                    teamMembers={props?.teamMembers}
                    caller={props?.sassCaller}
                    contractSentDisabled={!read_contract_sent_ready(props?.lead)}
                    onAction={props?.onProgressAction}
                    onDate={props?.onProgressActionDate}
                    onComment={props?.onProgressActionComment}
                    onCaller={props?.onSassCaller}
                />
            : (
                <FormBox>
                    <FormBoxSelection>
                        <p>{status}</p>
                    </FormBoxSelection>
                </FormBox>
            )}
            <Sales_Lead_Form_Session
                session={activeSalesSession}
                t={t}
            />
            <Sales_Lead_Form_Field
                field="firstname"
                label={t("Owner first name")}
                value={props?.lead?.firstname || ""}
                emptyLabel=""
                locked={formLocked || props?.lead?.contactAccess?.allowed === false}
                editingField={editingField}
                editingValue={editingValue}
                onStart={start_field_edit}
                onDraft={draft_field_edit}
                onDone={finish_field_edit}
            />
            <Sales_Lead_Form_Field
                field="lastname"
                label={t("Owner last name")}
                value={props?.lead?.lastname || ""}
                emptyLabel=""
                locked={formLocked || props?.lead?.contactAccess?.allowed === false}
                editingField={editingField}
                editingValue={editingValue}
                onStart={start_field_edit}
                onDraft={draft_field_edit}
                onDone={finish_field_edit}
            />
            <Sales_Lead_Form_Field
                field="brand"
                label={t("Brand")}
                value={props?.lead?.brand || ""}
                emptyLabel={t("No brand")}
                locked={formLocked}
                editingField={editingField}
                editingValue={editingValue}
                onStart={start_field_edit}
                onDraft={draft_field_edit}
                onDone={finish_field_edit}
            />
            <Sales_Lead_Form_Select_Field
                field="category"
                label={t("Category")}
                value={props?.lead?.category || ""}
                emptyLabel={t("No category")}
                locked={formLocked}
                options={categoryOptions}
                optionLabel={t}
                onChange={select_field_value}
            />
            <Sales_Lead_Form_Select_Field
                field="subcategory"
                label={t("Subcategory")}
                value={props?.lead?.subcategory || ""}
                emptyLabel={t("No subcategory")}
                locked={formLocked || !props?.lead?.category}
                options={subcategoryOptions}
                optionLabel={t}
                onChange={select_field_value}
            />
            <Sales_Lead_Form_Field
                field="location"
                label={t("Location")}
                value={props?.lead?.location || ""}
                emptyLabel=""
                locked={formLocked || props?.lead?.contactAccess?.allowed === false}
                editingField={editingField}
                editingValue={editingValue}
                onStart={start_field_edit}
                onDraft={draft_field_edit}
                onDone={finish_field_edit}
            />
            <Sales_Lead_Form_List_Field
                field="email"
                label={t("Email")}
                value={emails}
                values={emails}
                emptyLabel=""
                icon="Email"
                add={t("Add")}
                locked={formLocked || props?.lead?.contactAccess?.allowed === false}
                editingField={editingField}
                editingValue={editingValue}
                onStart={start_field_edit}
                onDraft={(value) => draft_list_value("email", value)}
                onAdd={add_list_value}
                onDelete={delete_list_value}
                onDone={finish_field_edit}
            />
            <Sales_Lead_Form_List_Field
                field="phone"
                label={t("Phone")}
                value={phones}
                values={phones}
                emptyLabel=""
                icon="Phone"
                add={t("Add")}
                locked={formLocked || props?.lead?.contactAccess?.allowed === false}
                editingField={editingField}
                editingValue={editingValue}
                onStart={start_field_edit}
                onDraft={(value) => draft_list_value("phone", value)}
                onAdd={add_list_value}
                onDelete={delete_list_value}
                onDone={finish_field_edit}
            />
            <Sales_Lead_Comments
                lead={props?.lead}
                locked={formLocked}
                status={status}
                onAddComment={props?.onAddComment}
            />
            <FormBox>
                <Builder_ split>
                    <FormBoxSelection>
                        <h5>{t("Active quests")}</h5>
                        <p>{Number(props?.lead?.questCounts?.active || props?.lead?.activeQuestCount || 0)}</p>
                    </FormBoxSelection>
                    <FormBoxSelection>
                        <h5>{t("Inactive quests")}</h5>
                        <p>{Number(props?.lead?.questCounts?.inactive || props?.lead?.inactiveQuestCount || 0)}</p>
                    </FormBoxSelection>
                </Builder_>
            </FormBox>
        </ProspectLeadFormBody>
    );
};
